import { memo, useEffect, useRef, useState } from "react";
import { Divider, Grid2, IconButton, Typography } from "@mui/material";
import { EditNote, Logout, PersonAdd } from "@mui/icons-material";

import Accordion from "../components/Accordion.js";
import { ThirdBackgroundButton } from "../components/Buttons.js";
import Form from "../components/Form.js";
import Spinner from "../components/Spinner.js";
import Tooltip from "../components/Tooltip.js";
import { useSnackbar } from "../utils/index.js";
import { addMember, createTeam, getTeam, leaveTeam, updateTeam } from "../api/index.js";
import Dialog from "../components/Dialog.js";
import Popup from "../components/Popup.js";

const Team = () => {
	const { error, success } = useSnackbar();
	const [team, setTeam] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [step, setStep] = useState("show");
	const [leaveTeamWarningOpen, setLeaveTeamWarningOpen] = useState(false);
	const [addMemberPopupOpen, setAddMemberPopupOpen] = useState(false);
	const [teamNamePopupOpen, setTeamNamePopupOpen] = useState(false);

	const formRef = useRef(null);
	const addMemberFormRef = useRef(null);
	const teamNameFormRef = useRef(null);

	const fetchTeam = async () => {
		setIsLoading(true);
		const { success: getSuccess, name: fetchedTeamName, members: fetchedTeamMembers, message } = await getTeam();
		if (getSuccess) {
			setTeam({ name: fetchedTeamName, members: fetchedTeamMembers });
		} else {
			error(message);
		}

		setIsLoading(false);
	};

	const onSubmit = async (values) => {
		setIsLoading(true);

		try {
			const { success: createSuccess, message, errors } = await createTeam(values);
			if (createSuccess) {
				success(message);
				setStep("show");
				fetchTeam();
			} else if (formRef.current) {
				error(message);
				for (const err of errors) {
					formRef.current.setError(err.field, err.message);
				}
			}
		} catch {
			error("Something went wrong");
		}

		setIsLoading(false);
	};

	const onMemberSubmit = async (values) => {
		setIsLoading(true);

		try {
			const { success: addSuccess, message, errors } = await addMember(values);
			if (addSuccess) {
				success(message);
				setAddMemberPopupOpen(false);
				fetchTeam();
			} else if (addMemberFormRef.current) {
				error(message);
				for (const err of errors) {
					addMemberFormRef.current.setError(err.field, err.message);
				}
			}
		} catch {
			error("Something went wrong");
		}

		setIsLoading(false);
	};

	const onLeaveTeam = async () => {
		setIsLoading(true);

		try {
			const { success: leaveSuccess, message } = await leaveTeam();
			if (leaveSuccess) {
				success(message);
				fetchTeam();
			} else {
				error("Something went wrong");
			}
		} catch {
			error("Something went wrong");
		}

		setIsLoading(false);
	};

	const onTeamSubmit = async (values) => {
		setIsLoading(true);

		try {
			const { success: updateSuccess, message, errors } = await updateTeam(values);
			if (updateSuccess) {
				success(message);
				setTeamNamePopupOpen(false);
				fetchTeam();
			} else if (teamNameFormRef.current) {
				error(message);
				for (const err of errors) {
					teamNameFormRef.current.setError(err.field, err.message);
				}
			}
		} catch {
			error("Something went wrong");
		}

		setIsLoading(false);
	};

	const formContent = [
		{
			customType: "input",
			id: "name",
			type: "text",
			label: "Team Name",
			placeholder: "Team Name",
		},
		{
			customType: "input",
			id: "user2",
			type: "email",
			label: "Second Team Member (optional)",
			placeholder: "User Email",
		},
		{
			customType: "input",
			id: "user3",
			type: "email",
			label: "Third Team Member (optional)",
			placeholder: "User Email",
		},
		{
			customType: "input",
			id: "user4",
			type: "email",
			label: "Fourth Team Member (optional)",
			placeholder: "User Email",
		},
		{
			customType: "input",
			id: "user5",
			type: "email",
			label: "Fifth Team Member (optional)",
			placeholder: "User Email",
		},
		{
			customType: "button",
			id: "submit",
			type: "submit",
			text: "Submit",
			color: "third",
		},
	];

	const addPersonFormContent = [
		{
			customType: "input",
			id: "email",
			type: "email",
			label: "User Email",
			placeholder: "User Email",
		},
		{
			customType: "button",
			id: "submit",
			type: "submit",
			text: "Add",
			color: "third",
		},
	];

	const teamNameFormContent = [
		{
			customType: "input",
			id: "name",
			type: "text",
			label: "Team Name",
			value: team?.name,
			placeholder: "Team Name",
		},
		{
			customType: "button",
			id: "submit",
			type: "submit",
			text: "Submit",
			color: "third",
		},
	];

	useEffect(() => {
		fetchTeam();
	}, []);
    
	return (
		<>
			<Spinner open={isLoading} />
			<Grid2 container display="flex" direction="column" justifyContent="center" alignItems="center">
				{!team?.name && step === "show" && (
					<Grid2 display="flex" flexDirection="column" alignItems="center" mt={5}>
						<Typography variant="h4" color="primary" mb={2}>{"No team found"}</Typography>
						<ThirdBackgroundButton onClick={() => setStep("create")} title="Create Team" />
					</Grid2>
				)}
				{step === "create" && (
					<Grid2 width="400px" display="flex" alignItems="center" flexDirection="column">
						<Form ref={formRef} content={formContent} validationSchema="teamCreation" onSubmit={onSubmit} />
					</Grid2>
				)}
				{team?.name && step === "show" && (
					<Grid2 mt={2} width="100%">
						<Accordion
							alwaysExpanded
							title={(
								<Grid2
									container
									width="100%"
									display="flex"
									direction="row"
									alignItems="center"
									justifyContent="space-between"
								>
									<Typography>{team.name}</Typography>
									<Grid2>
										<Tooltip title="Edit Team Name">
											<IconButton onClick={() => setTeamNamePopupOpen(true)}>
												<EditNote />
											</IconButton>
										</Tooltip>
										<Tooltip title="Add Member">
											<IconButton onClick={() => setAddMemberPopupOpen(true)}>
												<PersonAdd />
											</IconButton>
										</Tooltip>
										<Tooltip title="Leave Team">
											<IconButton onClick={() => setLeaveTeamWarningOpen(true)}>
												<Logout />
											</IconButton>
										</Tooltip>
									</Grid2>
								</Grid2>
							)}
							subtitle={(
								<Grid2 container display="flex" direction="row" alignItems="center" justifyContent="center" spacing={0}>
									<Grid2 size={{ xs: 3 }}>
										<Typography>{"Username"}</Typography>
									</Grid2>
									<Grid2 size={{ xs: 4 }} textAlign="center">
										<Typography>{"Full Name"}</Typography>
									</Grid2>
									<Grid2 size={{ xs: 5 }}>
										<Typography>{"E-mail"}</Typography>
									</Grid2>
								</Grid2>
							)}
							content={(
								<Grid2 container width="100%" display="flex" direction="column" alignItems="center" justifyContent="center" spacing={0} color="black">
									{team.members.map((us, ind) => (
										<Grid2 key={`comp_${ind}`} flexDirection="column" width="100%">
											{ind !== 0
											&& (
												<Grid2 key={`divider_${ind}`} my={1} width="100%">
													<Divider style={{ width: "100%" }} />
												</Grid2>
											)}
											<Grid2 key={`row_${ind}`} container alignItems="center">
												<Grid2 size={{ xs: 3 }}>
													<Typography>{us.username}</Typography>
												</Grid2>
												<Grid2 size={{ xs: 4 }} textAlign="center">
													<Typography>{us.fullname}</Typography>
												</Grid2>
												<Grid2 size={{ xs: 5 }}>
													<Typography>{us.email}</Typography>
												</Grid2>
											</Grid2>
										</Grid2>
									))}
								</Grid2>
							)}
						/>
					</Grid2>
				)}
			</Grid2>
			<Dialog
				open={leaveTeamWarningOpen}
				title="Leave Team"
				text="Are you sure you want to leave the team? You will not be able to rejoin the team unless invited again."
				confirmButton="Leave"
				cancelButton="Cancel"
				onConfirm={() => {
					setLeaveTeamWarningOpen(false);
					onLeaveTeam();
				}}
				onClose={() => setLeaveTeamWarningOpen(false)}
			/>
			<Popup
				width="400px"
				title="Add Member"
				open={addMemberPopupOpen}
				onClose={() => setAddMemberPopupOpen(false)}
			>
				<Form ref={addMemberFormRef} content={addPersonFormContent} validationSchema="addMember" onSubmit={onMemberSubmit} />
			</Popup>
			<Popup
				width="400px"
				title="Edit Team Name"
				open={teamNamePopupOpen}
				onClose={() => setTeamNamePopupOpen(false)}
			>
				<Form ref={teamNameFormRef} content={teamNameFormContent} validationSchema="teamName" onSubmit={onTeamSubmit} />
			</Popup>
		</>
	);
};

export default memo(Team);
